/**
 * Execution Plan Stack
 *
 * Renders all pending execution plan batches as a single unified plan.
 * All pending nodes from all batches are merged into one list with one
 * total sparks count and one "Let's go!" button that approves everything.
 *
 * Container is ``flex-shrink-0`` so it doesn't compete with the
 * messages area for vertical space, and the body is capped + scrolls
 * internally so very tall plans never push the chat input off screen.
 */

import { memo, useCallback, useState } from 'react'
import { Zap } from 'lucide-react'
import { ExecutionPlanCard } from './ExecutionPlanCard'
import type { ApprovalRef } from '../../generated/executionApprovals'
import type { ExecutionPlanBatch } from '../../hooks/useExecutionPlan'
import { useClickLock } from '../../hooks/useClickLock'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { useTranslation } from 'react-i18next'
import { trackExecutionPlan } from '../../analytics/interactionAnalytics'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'

const POST_CALL_COOLDOWN_MS = 1000

interface ExecutionPlanStackProps {
  batches: ExecutionPlanBatch[]
  onConfirm: (quotes: ApprovalRef[]) => void | Promise<void>
  onCancel: (batchId: string) => void | Promise<void>
  onNodeClick?: (nodeId: string) => void
}

export const ExecutionPlanStack = memo(function ExecutionPlanStack({
  batches,
  onConfirm,
  onCancel,
  onNodeClick,
}: ExecutionPlanStackProps) {
  const pendingBatches = batches.filter((b) => b.status === 'pending')
  const { t } = useTranslation('chat')
  const projectId = useOptionalProjectId()
  const [folded, setFolded] = useLocalStorage<boolean>('execution-plan-folded', false)
  const { isLocked, run } = useClickLock(POST_CALL_COOLDOWN_MS)
  const [error, setError] = useState<{ message: string; action: 'confirm' | 'cancel' } | null>(null)

  const locked = isLocked('__all__')

  const handleConfirmAll = useCallback(() => {
    setError(null)
    void run('__all__', async () => {
      try {
        await onConfirm(pendingBatches.map((batch) => batch.quote))
      } catch (err) {
        setError({
          message: err instanceof Error ? err.message : t('executionPlanStack.confirmFailed'),
          action: 'confirm',
        })
        throw err
      }
    })
  }, [run, pendingBatches, onConfirm, t])

  const handleCancelAll = useCallback(() => {
    setError(null)
    void run('__all__', async () => {
      try {
        for (const b of pendingBatches) {
          await onCancel(b.batchId)
        }
      } catch (err) {
        setError({
          message: err instanceof Error ? err.message : t('executionPlanStack.cancelFailed'),
          action: 'cancel',
        })
        throw err
      }
    })
  }, [run, pendingBatches, onCancel, t])

  if (pendingBatches.length === 0) return null

  const allNodes = pendingBatches.flatMap((b) =>
    b.entries.map((e) => ({
      node_id: e.nodeId,
      type: e.type,
      action: e.action,
      rerunContext: e.rerunContext,
      label: e.label,
      sparks: e.sparks,
    })),
  )
  const totalSparks = pendingBatches.reduce((sum, b) => sum + b.totalSparks, 0)

  return (
    <div className="px-3 pb-2 flex-shrink-0 max-h-[35vh] flex flex-col">
      <div className="rounded-lg border border-chrome/30 bg-chrome/[0.03] overflow-hidden flex flex-col min-h-0 shadow-sm shadow-chrome/10">
        {/* Title row */}
        <div className="px-3 py-2 bg-chrome/[0.06] border-b border-chrome/20 flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-chrome animate-pulse flex-shrink-0" />
          <span className="text-xs font-semibold text-chrome uppercase tracking-wider flex-shrink-0">
            {t('executionPlanStack.title')}
          </span>
          <div className="flex-1" />

          {folded && (
            <span className="text-xs text-chrome/70 font-medium flex-shrink-0 inline-flex items-center gap-1">
              {totalSparks}
              <Zap className="h-3 w-3" aria-hidden="true" />
              {t('executionPlanStack.pending')}
            </span>
          )}

          <button
            type="button"
            onClick={() => setFolded(!folded)}
            aria-label={folded ? t('executionPlanStack.expandPlan') : t('executionPlanStack.collapsePlan')}
            aria-expanded={!folded}
            className="flex-shrink-0 w-5 h-5 inline-flex items-center justify-center rounded text-text-muted hover:text-text-primary hover:bg-surface-2 transition-colors"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: folded ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 150ms ease-out',
              }}
            >
              <polyline points="3 4.5 6 7.5 9 4.5" />
            </svg>
          </button>
        </div>

        {!folded && (
          <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
            <ExecutionPlanCard
              hideHeader
              nodes={allNodes}
              totalSparks={totalSparks}
              disabled={locked}
              loading={locked}
              onSelect={(option) => {
                // The live cost-approval card (YDoc / REST execution plans).
                if (projectId) trackExecutionPlan(projectId, option, allNodes.length, totalSparks)
                if (option === 'confirm') {
                  handleConfirmAll()
                } else {
                  handleCancelAll()
                }
              }}
              onNodeClick={onNodeClick}
            />
            {error && (
              <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-500/30 flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-red-400">{error.message}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setError(null)
                      if (error.action === 'confirm') {
                        handleConfirmAll()
                      } else {
                        handleCancelAll()
                      }
                    }}
                    className="mt-1 text-xs text-chrome hover:text-chrome/80 font-medium"
                  >
                    {t('executionPlanStack.retry')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})
